import { Button, Css, Tooltip, useComputed, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ReactNode, useCallback } from "react";
import { queryResult } from "src/utils";
import {
  SelectConfigurationStep_StructureFragment,
  useSelectConfigurationStep_SelectedConfigQuery,
} from "../1.SelectConfigurationStep.generated";
import { ApplyConfigurationFormInput } from "../applyConfigurationForm";
import { ApplyConfigurationCard } from "./ApplyConfigurationCard";
import { ApplyConfigurationCard_CheckoutConfigFragment } from "./ApplyConfigurationCard.generated";
import { NoConfigurationEmptyState } from "./NoConfigurationEmptyState";
import { SelectConfigurationModal } from "./SelectConfigurationModal";

type ConfigurationChangeSectionProps = {
  formState: ObjectState<ApplyConfigurationFormInput>;
  structure: SelectConfigurationStep_StructureFragment;
};

export function ConfigurationChangeSection(props: ConfigurationChangeSectionProps) {
  const { formState, structure } = props;
  const checkoutConfigId = useComputed(() => formState.checkoutConfigId.value, [formState]);
  const query = useSelectConfigurationStep_SelectedConfigQuery({
    variables: { configId: checkoutConfigId! },
    skip: !checkoutConfigId,
  });
  if (!checkoutConfigId) {
    return <ConfigurationChangeSectionView formState={formState} structure={structure} />;
  } else {
    return queryResult(query, ({ checkoutConfig }) => (
      <ConfigurationChangeSectionView formState={formState} structure={structure} replacementConfig={checkoutConfig} />
    ));
  }
}

type ConfigurationChangeSectionViewProps = ConfigurationChangeSectionProps & {
  replacementConfig?: ApplyConfigurationCard_CheckoutConfigFragment;
};
function ConfigurationChangeSectionView(props: ConfigurationChangeSectionViewProps) {
  const { formState, structure, replacementConfig } = props;
  const originalConfig = structure.readyPlanConfig;
  const hasOriginalConfigPlan = !!originalConfig?.readyPlan;
  const { openModal } = useModal();

  const chooseReplacement = useCallback(() => {
    openModal({
      size: "xxl",
      content: (
        <SelectConfigurationModal
          structure={structure}
          initialSelectedConfigId={replacementConfig?.id}
          onSelect={(config) => {
            const readyPlanId = (structure.isAdu ? config.aduProductOffering : config.productOffering)?.id;
            formState.set({
              checkoutConfigId: config.id,
              readyPlanId,
              proposedReadyPlanOptions: config.readyPlanOptions
                .filter((rpo) => rpo.readyPlan.id === readyPlanId)
                .map((rpo) => ({ readyPlanOptionId: rpo.id })),
              // A new configuration needs a new dry run, so the steps can't serve the previous one's preview
              dryRunChangeEventId: undefined,
            });
          }}
        />
      ),
    });
  }, [openModal, formState, structure, replacementConfig]);

  if (!hasOriginalConfigPlan && !replacementConfig) {
    return <NoConfigurationEmptyState onClick={chooseReplacement} />;
  } else {
    return (
      <div css={Css.df.gap3.mb3.$}>
        <ConfigurationPanel title="Original">
          {hasOriginalConfigPlan ? (
            <ApplyConfigurationCard structure={structure} readyPlanConfig={originalConfig} />
          ) : (
            <NoConfigurationEmptyState compact />
          )}
        </ConfigurationPanel>

        <ConfigurationPanel title="Replacement">
          {replacementConfig ? (
            <ApplyConfigurationCard
              structure={structure}
              readyPlanConfig={replacementConfig}
              onClick={chooseReplacement}
              onRemove={() =>
                // Dropping the replacement drops everything derived from it
                formState.set({
                  checkoutConfigId: undefined,
                  readyPlanId: undefined,
                  proposedReadyPlanOptions: [],
                  dryRunChangeEventId: undefined,
                })
              }
            />
          ) : (
            <ChooseReplacementButton onClick={chooseReplacement} />
          )}
        </ConfigurationPanel>
      </div>
    );
  }
}

function ConfigurationPanel(props: { title: string; children: ReactNode }) {
  const { title, children } = props;
  return (
    <div css={Css.df.fdc.gap2.fb0.fg1.mw0.$}>
      <span css={Css.mdSb.$}>{title}</span>
      <div css={Css.df.fg1.$}>{children}</div>
    </div>
  );
}

function ChooseReplacementButton(props: { onClick: () => void }) {
  const { onClick } = props;
  return (
    <div css={Css.df.aic.jcc.w100.ba.bcGray300.br8.bgWhite.p3.$}>
      <Tooltip title="Select the configuration to apply to this structure">
        <Button label="Choose Replacement" variant="secondary" onClick={onClick} />
      </Tooltip>
    </div>
  );
}
